/**
 * Registry síncrono de dados de baixa cardinalidade.
 *
 * Por que isso existe: `getProject`, `getPerson`, `getAM`, `osOf` e
 * `projectLabel` são helpers **síncronos chamados inline dentro do JSX** em
 * todas as rotas e também dentro dos engines. Não existe caminho para
 * torná-los assíncronos um a um sem reescrever o JSX de onze telas.
 *
 * A saída é mantê-los síncronos, alimentados por um registry de módulo que
 * uma única query de bootstrap hidrata no boot e o WebSocket invalida.
 * O TanStack Query fica para os dados de volume — alocações, projetos,
 * solicitações, notificações.
 */

import { useSyncExternalStore } from "react";

export interface ProfissionalRegistry {
  id: string;
  nome: string;
  inicial: string;
  email: string;
  celular?: string | null;
  funcao: string;
  tipo: "INTERNO" | "FREELANCER";
  senioridade: "JUNIOR" | "PLENO" | "SENIOR" | "ESPECIALISTA";
  nivelOcupacaoAlvo: "ALTA" | "MEDIA" | "BAIXA";
  sensivelGaps: boolean;
  capacidadeSemanalSlots: number;
  cor?: string | null;
  ordemExibicao: number;
  ativo: boolean;
  clientesPreferenciais: string[];
  inatividades: { inicio: string; fim: string }[];
}

export interface ClienteRegistry {
  id: string;
  nome: string;
  ativo: boolean;
}

export interface PerfilRegistry {
  id: string;
  nome: string;
  vinculo: "INTERNO" | "EXTERNO";
  base: boolean;
  telas: string[];
  permissoes: string[];
  escopoPipeline: "TODOS" | "PROPRIOS";
  escopoFlow: "COMPLETA" | "PROPRIA_LINHA";
  escopoCard: "COMPLETO" | "PROPRIA_FASE";
}

export interface UsuarioRegistry {
  id: string;
  nome: string;
  email: string;
  perfilId: string;
  profissionalId: string | null;
  /** Permissões e escopos vêm resolvidos do perfil — o servidor manda, o
   *  cliente apenas espelha para esconder o que não faz sentido mostrar. */
  permissoes: string[];
  escopoPipeline: "TODOS" | "PROPRIOS";
  escopoFlow: "COMPLETA" | "PROPRIA_LINHA";
  escopoCard: "COMPLETO" | "PROPRIA_FASE";
}

/**
 * Índice enxuto de projetos.
 *
 * A grade, o Gantt e o pipeline precisam resolver OS, cliente e cor do
 * coordenador para cada slot — sem isto o rótulo do slot mostraria o id bruto.
 * O card carrega o projeto completo pelo endpoint próprio.
 */
export interface ProjetoRegistry {
  id: string;
  os: string;
  nome: string;
  clienteId: string;
  frente: string;
  tipoProjeto: string;
  estagio: string;
  coordenadorId: string | null;
  comercialId: string | null;
  pausado: boolean;
  /** Está na coluna final do kanban. */
  encerrado: boolean;
}

export interface Bootstrap {
  usuario: UsuarioRegistry | null;
  profissionais: ProfissionalRegistry[];
  clientes: ClienteRegistry[];
  perfis: PerfilRegistry[];
  projetos: ProjetoRegistry[];
  /** Conjunto de parâmetros vigente (M10). */
  parametros: Record<string, unknown>;
  /** Feriados e pontos facultativos, em ISO `yyyy-mm-dd`. */
  diasNaoUteis: string[];
}

interface Estado {
  usuario: UsuarioRegistry | null;
  profissionais: Map<string, ProfissionalRegistry>;
  clientes: Map<string, ClienteRegistry>;
  perfis: Map<string, PerfilRegistry>;
  projetos: Map<string, ProjetoRegistry>;
  parametros: Record<string, unknown>;
  diasNaoUteis: Set<string>;
  hidratado: boolean;
  /// Incrementa a cada hidratação — permite memoizar derivações do registry
  /// sem depender de identidade de referência, que os getters não preservam.
  versao: number;
}

const estado: Estado = {
  usuario: null,
  profissionais: new Map(),
  clientes: new Map(),
  perfis: new Map(),
  projetos: new Map(),
  parametros: {},
  diasNaoUteis: new Set(),
  hidratado: false,
  versao: 0,
};

const ouvintes = new Set<() => void>();

export function hidratarRegistry(dados: Bootstrap) {
  estado.usuario = dados.usuario;
  estado.profissionais = new Map(dados.profissionais.map((p) => [p.id, p]));
  estado.clientes = new Map(dados.clientes.map((c) => [c.id, c]));
  estado.perfis = new Map(dados.perfis.map((p) => [p.id, p]));
  estado.projetos = new Map((dados.projetos ?? []).map((p) => [p.id, p]));
  estado.parametros = dados.parametros;
  estado.diasNaoUteis = new Set(dados.diasNaoUteis);
  estado.hidratado = true;
  estado.versao += 1;
  ouvintes.forEach((o) => o());
}

export function assinarRegistry(ouvinte: () => void) {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

export const registryHidratado = () => estado.hidratado;
export const versaoRegistry = () => estado.versao;

/** Re-renderiza quem lê o registry inline quando o bootstrap é reidratado. */
export const useVersaoRegistry = () =>
  useSyncExternalStore(assinarRegistry, versaoRegistry, versaoRegistry);

// ── Acesso síncrono ──────────────────────────────────────────

export const usuarioAtual = () => estado.usuario;

export const getProfissional = (id: string | null | undefined) =>
  id ? estado.profissionais.get(id) : undefined;

export const listarProfissionais = () =>
  [...estado.profissionais.values()].sort((a, b) => a.ordemExibicao - b.ordemExibicao);

export const getCliente = (id: string | null | undefined) =>
  id ? estado.clientes.get(id) : undefined;

export const listarClientes = () =>
  [...estado.clientes.values()].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

export const getPerfil = (id: string | null | undefined) =>
  id ? estado.perfis.get(id) : undefined;

export const listarPerfis = () => [...estado.perfis.values()];

export const getProjeto = (id: string | null | undefined) =>
  id ? estado.projetos.get(id) : undefined;

export const listarProjetos = () => [...estado.projetos.values()];

export function parametro<T>(chave: string, padrao: T): T {
  const valor = estado.parametros[chave];
  return (valor as T) ?? padrao;
}

/**
 * A grade permite alocação manual em fim de semana; o motor de cronograma só
 * distribui automaticamente em dia útil. Os dois comportamentos convivem de
 * propósito — ver CLAUDE.md.
 */
export function ehDiaUtil(dataISO: string): boolean {
  if (estado.diasNaoUteis.has(dataISO)) return false;
  const diaSemana = new Date(`${dataISO}T00:00:00Z`).getUTCDay();
  return diaSemana !== 0 && diaSemana !== 6;
}

/** Só para testes — devolve o registry ao estado vazio. */
export function limparRegistry() {
  estado.usuario = null;
  estado.profissionais.clear();
  estado.clientes.clear();
  estado.perfis.clear();
  estado.projetos.clear();
  estado.parametros = {};
  estado.diasNaoUteis.clear();
  estado.hidratado = false;
  estado.versao += 1;
}
